/**
 * Converts a string into a URL-friendly "slug" format.
 *
 * The `slugify` function processes a given string and returns a formatted string
 * suitable for use in URLs or file names. It performs the following operations:
 *
 * 1. Normalizes the input string using Unicode normalization format 'NFD',
 *    decomposing combined characters into their individual components.
 * 2. Removes any diacritical marks (e.g., accents) from characters.
 * 3. Converts all characters to lowercase.
 * 4. Trims leading and trailing whitespace.
 * 5. Removes any non-alphanumeric or non-space characters (excluding dashes).
 * 6. Replaces all spaces with dashes (`-`).
 * 7. Ensures no consecutive dashes (`--`).
 *
 * If the input string is `null` or empty, the function returns an empty string.
 *
 * @param value - The input string to be converted into a slug.
 * @returns A URL-friendly, slugified version of the input string.
 *
 * @example
 * ```typescript
 * slugify("Hello, World!"); // Returns "hello-world"
 * slugify("  This is -- a Test!  "); // Returns "this-is-a-test"
 * slugify("Accénted Téxt 123"); // Returns "accented-text-123"
 * slugify(""); // Returns ""
 * ```
 */
export const slugify = (value: string): string => {
  if (!value) return '';

  return value
    .normalize('NFD') // Decomposes accented characters
    .replace(/[\u0300-\u036f]/g, '') // Removes accents
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9 -]/g, '') // Removes special characters
    .replace(/\s+/g, '-') // Replaces spaces with dashes
    .replace(/-+/g, '-'); // Avoids consecutive dashes
};
